import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { ConfigProvider, Mentions } from "antd";
import { MENTION_BG, BRAND } from "./mention-style";

/**
 * Input de nota con la mención pintada como chip (verde sobre tinte del mismo verde), igual que en
 * el timeline.
 *
 * Un `<textarea>` no puede tener texto con estilos por tramo, así que va la técnica del espejo: un
 * div detrás con la misma tipografía y la misma caja dibuja el texto con los chips, y el textarea
 * encima queda con su texto transparente (el caret sí se pinta). Lo que se ve es el espejo; lo que
 * se edita es el textarea de siempre — el autocomplete de `@`, el teclado y el `autoSize` de antd
 * quedan intactos.
 *
 * 🔴 Dos cosas que hacen que esto funcione y que son fáciles de romper:
 *
 * 1. **Las métricas se COPIAN del textarea real** (`getComputedStyle`), no se replican a mano.
 *    antd 5 no expone el estilo del textarea interno de `Mentions` —`style` va al wrapper—, así que
 *    fijar font/padding "iguales" desde afuera no llega al elemento que importa y el espejo termina
 *    corrido. Copiarlas es lo único que garantiza que coincidan.
 * 2. **Lo que el chip agrega tiene que ser NEUTRO en métricas.** El padding horizontal se cancela
 *    con un margin negativo del mismo valor, y NO va `italic` ni `font-weight` (los del chip del
 *    timeline): los dos cambian el ancho de los glifos y desalinean el espejo caracter a caracter.
 *    Es la única diferencia con el render de la nota, y es a propósito.
 *
 * El color transparente del textarea y el fondo del wrapper viven en `config.css`
 * (`.hist-mentions`): son estados del DOM interno de antd y desde React no hay handle.
 */

/** Color de texto de antd (`colorText`) para el espejo — el textarea real va transparente. */
const TEXT_COLOR = "rgba(0, 0, 0, 0.88)";

/** Propiedades que definen dónde cae cada glifo. Si alguna difiere, el espejo se corre. */
const COPIED_STYLE = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontVariant",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textIndent",
  "textTransform",
  "tabSize",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "boxSizing",
  "whiteSpace",
  "overflowWrap",
  "wordBreak",
] as const;

interface Props {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: React.ReactNode; key: string }[];
  /** Nombres a resaltar — los mencionables del registro. */
  names: string[];
  placeholder?: string;
  autoSize?: { minRows: number; maxRows: number };
  variant?: "outlined" | "borderless";
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Enter en el textarea. Lo usa el compose de Conversaciones para enviar sin Shift. */
  onPressEnter?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  style?: React.CSSProperties;
  /** Overrides colorPrimary for the focus/hover border — prevents the global green from bleeding in. */
  focusBorderColor?: string;
}

/** Tramos de texto: los `@Nombre` conocidos salen como chip. */
function highlight(text: string, names: string[]): React.ReactNode[] {
  if (!names.length) return [text];
  const escaped = names
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const re = new RegExp(`@(${escaped})(?![\\p{L}\\p{N}])`, "gu");
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    out.push(
      <span
        key={idx}
        style={{
          color: BRAND,
          backgroundColor: MENTION_BG,
          borderRadius: 4,
          // El padding se cancela con el margin: pinta más ancho sin correr el texto.
          padding: "1px 4px",
          margin: "0 -4px",
        }}
      >
        {m[0]}
      </span>,
    );
    last = idx + m[0].length;
  }
  out.push(text.slice(last));
  return out;
}

export const MentionsInput: React.FC<Props> = ({
  value,
  onChange,
  options,
  names,
  placeholder,
  autoSize,
  variant,
  autoFocus,
  onKeyDown,
  onPressEnter,
  style,
  focusBorderColor,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);

  /** Copia métricas y geometría del textarea real al espejo. */
  const sync = useCallback(() => {
    const wrap = wrapRef.current;
    const mirror = mirrorRef.current;
    const ta = wrap?.querySelector("textarea");
    if (!wrap || !mirror || !ta) return;

    const cs = window.getComputedStyle(ta);
    for (const prop of COPIED_STYLE) {
      mirror.style[prop as any] = cs[prop as any];
    }
    // El textarea envuelve como `pre-wrap`; el espejo tiene que hacer lo mismo aunque el
    // computed traiga otra cosa (un `nowrap` heredado cambiaría los saltos de línea).
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.borderStyle = "solid";
    mirror.style.borderColor = "transparent";

    // Geometría por rect y no por offsetTop: el wrapper de antd puede o no ser el offsetParent,
    // y la diferencia se paga en píxeles de desalineación.
    const wr = wrap.getBoundingClientRect();
    const tr = ta.getBoundingClientRect();
    mirror.style.top = `${tr.top - wr.top}px`;
    mirror.style.left = `${tr.left - wr.left}px`;
    mirror.style.width = `${tr.width}px`;
    mirror.style.height = `${tr.height}px`;
    mirror.scrollTop = ta.scrollTop;
  }, []);

  // Con foco automático el caret va al final: el compose de Conversaciones entra al modo nota con
  // un `@` ya escrito, y antd abre el autocomplete según el texto ANTES del caret.
  useLayoutEffect(() => {
    if (!autoFocus) return;
    const ta = wrapRef.current?.querySelector("textarea");
    ta?.setSelectionRange(ta.value.length, ta.value.length);
    // Sólo al montar: después el caret es del operador.
  }, []);

  // El `autoSize` de antd cambia la altura al tipear, así que hay que resincronizar en cada
  // cambio de valor, y ante cualquier resize del textarea (ventana, panel, zoom).
  useEffect(() => {
    sync();
    const ta = wrapRef.current?.querySelector("textarea");
    if (!ta || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => sync());
    ro.observe(ta);
    return () => ro.disconnect();
  }, [sync, value]);

  return (
    <div
      ref={wrapRef}
      className="hist-mentions"
      // `data-filled` lo lee el CSS: con el input vacío el texto NO va transparente, si no
      // desaparece el placeholder.
      data-filled={value ? "true" : "false"}
      style={{ position: "relative", flex: 1, minWidth: 0, ...style }}
    >
      <div
        ref={mirrorRef}
        aria-hidden
        className="hist-mentions-mirror"
        style={{ color: TEXT_COLOR }}
      >
        {highlight(value, names)}
      </div>
      <ConfigProvider
        theme={
          focusBorderColor
            ? { token: { colorPrimary: focusBorderColor, colorPrimaryHover: focusBorderColor } }
            : undefined
        }
      >
        <Mentions
          value={value}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          autoSize={autoSize}
          variant={variant}
          autoFocus={autoFocus}
          onKeyDown={onKeyDown}
          onPressEnter={onPressEnter}
          onScroll={sync}
          style={{ width: "100%" }}
        />
      </ConfigProvider>
    </div>
  );
};
